/**
 * **Penyusun set menu di layar kasir** — stepper satu grup per langkah (Imron, 2026-08-07).
 *
 * ### ⭐ Kenapa DI DALAM container menu, bukan dialog melayang
 * Imron: *"kamu pakai box container menu dan meja saja. draw disitu, ada tombol next dan back."*
 *
 * Modifier memakai dialog karena pertanyaannya **satu layar dan pendek** — "pedas atau tidak" tak
 * boleh dilewati, jadi ia menutupi segalanya. Set menu berbeda bentuk: 3–6 langkah, tiap langkah
 * berisi kisi produk seukuran kisi menu. Dialog melayang seukuran itu **menutupi bill**, dan bill
 * adalah satu-satunya tempat kasir bisa melihat apa yang sudah dipesan tamu selagi ia menyusun
 * paket berikutnya. Container yang sama dengan menu & meja menukar isi kolom kiri saja — kolom
 * kanan tak pernah bergerak.
 *
 * ### ⛔ Kontrolnya tetap milik SERVER
 * `validateComboSelections` di server yang menolak min/max (`ORDER-409-COMBO_INVALID`), dan sejak
 * KB16 `addLine` juga menolak set menu yang mencoba lewat jalur biasa. Yang di sini cuma membuat
 * penolakan itu **tak perlu terjadi di depan tamu** — bukan menggantikannya.
 *
 * ### ⭐⭐ Jumlah per opsi DITAMPILKAN, karena di situlah VTEC dulu gagal
 * "Pilih roti canai ayam dapat 2, beef dapat 1" di VTEC diakali `min=1 max=2`, yang menyerahkan
 * jumlahnya ke ingatan pelayan — *"yang protes orang dapur"*. Di sini `defaultQty` datang dari
 * master dan tercetak di kartunya (`×2`), jadi yang menentukan bukan lagi siapa yang mengetuk.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ComboGroup, ComboSelectionInput, MenuItem } from '../api/client';

export interface ComboPickerProps {
  readonly combo: MenuItem;
  /** Grup milik combo ini, URUT — urutannya dari `combo_product_group.sort_order`. */
  readonly groups: readonly ComboGroup[];
  readonly formatPrice: (canonical: string) => string;
  readonly onCancel: () => void;
  /**
   * @param price harga paket SESUDAH upcharge — dipakai layar untuk baris bill.
   *   ⚠️ Angka final tetap dari server; ini yang DILIHAT tamu sebelum paketnya masuk bill, dan
   *   keduanya memakai bilangan yang sama (harga dasar + Σ upcharge master).
   */
  readonly onConfirm: (
    selections: readonly ComboSelectionInput[],
    labels: readonly string[],
    price: string,
  ) => void;
}

/** Menjumlah desimal kanonik sederhana (4 desimal) tanpa menyentuh float. */
function addCanonical(a: string, b: string): string {
  const scale = (v: string): bigint => {
    const [w = '0', f = ''] = v.split('.');
    return BigInt(`${w}${f.padEnd(4, '0').slice(0, 4)}`);
  };
  const sum = scale(a) + scale(b);
  const s = (sum < 0n ? -sum : sum).toString().padStart(5, '0');
  return `${sum < 0n ? '-' : ''}${s.slice(0, -4)}.${s.slice(-4)}`;
}

export function ComboPicker(p: ComboPickerProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  /** `groupCode` → kode produk terpilih. */
  const [picked, setPicked] = useState<Record<string, string[]>>(() => {
    /*
     * ⭐ Bawaan master dipasang DI DEPAN, bukan layar kosong. Set menu yang tiap komponennya sudah
     * ditentukan (grup ber-satu-opsi) jadi tinggal ditekan Lanjut — dan yang memang harus dipilih
     * tetap terlihat karena kartunya menyala.
     */
    const init: Record<string, string[]> = {};
    for (const g of p.groups) {
      const def = g.options.filter((o) => o.isDefault).map((o) => o.productCode);
      if (def.length > 0) init[g.groupCode] = def.slice(0, Math.max(1, g.maxSelect));
    }
    return init;
  });

  const group = p.groups[step];
  const chosen = group === undefined ? [] : (picked[group.groupCode] ?? []);
  const enough = group === undefined || chosen.length >= group.minSelect;
  const last = step >= p.groups.length - 1;

  /**
   * ⭐⭐ Harga paket berjalan = harga dasar + Σ upcharge pilihan.
   *
   * ⛔ Ditampilkan karena kasir MEMBACAKANNYA ke tamu sebelum paketnya masuk bill. Upcharge yang
   * baru terlihat sesudah "Hitung Bill" adalah tagihan yang tak bisa dijelaskan siapa pun.
   * ⚠️ Server tetap yang menghitung angka final; ini perkiraan yang memakai bilangan yang sama.
   */
  const runningPrice = useMemo(() => {
    let total = p.combo.price;
    for (const g of p.groups) {
      for (const code of picked[g.groupCode] ?? []) {
        const o = g.options.find((x) => x.productCode === code);
        if (o !== undefined) total = addCanonical(total, o.upcharge);
      }
    }
    return total;
  }, [p.combo.price, p.groups, picked]);

  const toggle = (g: ComboGroup, code: string) => {
    setPicked((s) => {
      const cur = s[g.groupCode] ?? [];
      if (cur.includes(code)) {
        /*
         * ⛔ Pada grup `max = 1` ketukan kedua TIDAK mengosongkan: grup wajib yang jadi kosong
         * membuat tombol Lanjut mati tanpa sebab yang terlihat, dan kasir mengira layarnya macet.
         * Mengganti pilihan tetap satu ketukan — yang tak ada cuma cara membuatnya kosong.
         */
        if (g.maxSelect === 1) return s;
        return { ...s, [g.groupCode]: cur.filter((c) => c !== code) };
      }
      // `max = 1` berperilaku radio: yang lama DIGANTI, bukan ketukan yang ditolak.
      if (g.maxSelect === 1) return { ...s, [g.groupCode]: [code] };
      if (g.maxSelect > 0 && cur.length >= g.maxSelect) return s;
      return { ...s, [g.groupCode]: [...cur, code] };
    });
  };

  const confirm = () => {
    const out: ComboSelectionInput[] = [];
    const labels: string[] = [];
    for (const g of p.groups) {
      for (const code of picked[g.groupCode] ?? []) {
        out.push({ groupCode: g.groupCode, productCode: code });
        const o = g.options.find((x) => x.productCode === code);
        const qty = Number(o?.defaultQty ?? '1');
        /* ⚠️ Label menyebut jumlah HANYA bila bukan 1 — "1× Nasi" cuma derau di bill. */
        labels.push(qty > 1 ? `${String(qty)}× ${o?.productName ?? code}` : (o?.productName ?? code));
      }
    }
    p.onConfirm(out, labels, runningPrice);
  };

  /*
   * ⚠️ Set menu tanpa grup sama sekali TIDAK boleh sampai ke sini — server menolaknya
   * (`ORDER-404-NOT_FOUND`, KB8). Kalau toh katalognya basi, layar mengatakannya alih-alih
   * memberi stepper kosong yang tombolnya tak berbuat apa-apa.
   */
  if (p.groups.length === 0) {
    return (
      <div className="combo-pane" data-testid="combo-pane">
        <header className="combo-head">
          <h2>{p.combo.name}</h2>
        </header>
        <p className="empty" data-testid="combo-no-group">{t('cashier.combo.noGroup')}</p>
        <div className="combo-actions">
          <button type="button" data-testid="combo-batal" onClick={p.onCancel}>
            {t('cashier.combo.cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="combo-pane" data-testid="combo-pane">
      <header className="combo-head">
        <h2>{p.combo.name}</h2>
        {/* ⭐ "Langkah 2 dari 3" menjawab "masih berapa lagi?" sebelum ditanyakan — pertanyaan
            yang selalu muncul saat tamu menunggu di depan kasir. */}
        <span className="combo-step" data-testid="combo-step">
          {t('cashier.combo.step', { n: step + 1, total: p.groups.length })}
        </span>
        <b className="combo-price" data-testid="combo-price">{p.formatPrice(runningPrice)}</b>
      </header>

      {/*
        ⭐ Titik langkah bisa DIKETUK untuk melompat mundur — kasir yang salah pilih di langkah 1
        tak perlu menekan Kembali tiga kali. ⛔ Melompat MAJU tak diizinkan: langkah yang dilewati
        adalah grup wajib yang tak terjawab, dan itu persis yang ditolak server.
      */}
      <nav className="combo-dots" aria-label={t('cashier.combo.steps')}>
        {p.groups.map((g, i) => (
          <button
            key={g.groupCode}
            type="button"
            className={i === step ? 'combo-dot on' : 'combo-dot'}
            data-testid={`combo-dot-${g.groupCode}`}
            disabled={i > step}
            onClick={() => { setStep(i); }}
          >
            {g.groupName}
          </button>
        ))}
      </nav>

      {group !== undefined && (
        <section className="combo-group" data-testid={`combo-grup-${group.groupCode}`}>
          <h3>
            {group.groupName}
            <span className="combo-rule" data-testid={`combo-rule-${group.groupCode}`}>
              {/* ⭐ Aturannya disebut DI DEPAN, bukan ditemukan saat tombolnya tetap mati. */}
              {group.minSelect === group.maxSelect
                ? t('cashier.combo.pickExactly', { n: group.minSelect })
                : t('cashier.combo.pickRange', { min: group.minSelect, max: group.maxSelect })}
            </span>
          </h3>

          <div className="combo-options">
            {group.options.map((o) => {
              const on = chosen.includes(o.productCode);
              const qty = Number(o.defaultQty);
              return (
                <button
                  key={o.productCode}
                  type="button"
                  className={on ? 'combo-option on' : 'combo-option'}
                  data-testid={`combo-opsi-${o.productCode}`}
                  aria-pressed={on}
                  onClick={() => { toggle(group, o.productCode); }}
                >
                  <span className="combo-option-name">
                    {/* ⭐⭐ Jumlah per opsi — inilah yang dulu hidup di ingatan pelayan. */}
                    {qty > 1 && (
                      <b className="combo-qty" data-testid={`combo-qty-${o.productCode}`}>
                        {qty}×
                      </b>
                    )}
                    {o.productName}
                  </span>
                  {/* ⚠️ Upcharge tampil apa adanya: tambahan yang diam adalah tagihan yang tak
                      bisa dijelaskan kasir saat tamu bertanya. */}
                  {o.upcharge !== '0.0000' && (
                    <span className="combo-upcharge" data-testid={`combo-tambah-${o.productCode}`}>
                      +{p.formatPrice(o.upcharge)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="combo-actions">
        {/*
          ⭐ Kembali di langkah PERTAMA berarti batal — satu tombol, satu tempat. Tombol "Batal"
          tersendiri di sebelahnya membuat dua sasaran bersebelahan yang akibatnya sangat berbeda,
          dan jari yang meleset membuang seluruh susunan.
        */}
        <button
          type="button"
          data-testid="combo-kembali"
          onClick={() => { if (step === 0) p.onCancel(); else setStep(step - 1); }}
        >
          {step === 0 ? t('cashier.combo.cancel') : t('cashier.combo.back')}
        </button>
        <button
          type="button"
          className="action-primary"
          data-testid={last ? 'combo-tambah' : 'combo-lanjut'}
          disabled={!enough}
          title={enough ? undefined : t('cashier.combo.stillRequired', {
            group: group?.groupName ?? '', n: group?.minSelect ?? 0,
          })}
          onClick={() => { if (last) confirm(); else setStep(step + 1); }}
        >
          {last ? t('cashier.combo.add') : t('cashier.combo.next')}
        </button>
      </div>
    </div>
  );
}
